/// <amd-module name="Feed/Legacy/Components/Settings/MapIFeedSettingsStateToProps" />
import { findIndex } from "OfficeFabric/Utilities";

import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssPageService } from "VSS/Platform/Page";

import { Filter } from "VSSUI/Utilities/Filter";

import { FeedSettingsComponents, SettingsFilterItemKey } from "Feed/Common/Constants/Constants";
import { FeedPermission, FeedRetentionPolicy, FeedView, UpstreamSource } from "Feed/Common/Contracts/Feed";
import * as PackageResources from "Feed/Common/Resources";
import { IFeedDetailsPaneProps } from "Feed/Legacy/Components/Settings/FeedDetailsPane";
import { IFeedSettingsState } from "Feed/Legacy/Components/Settings/IFeedSettingsState";
import { IPermissionsProps } from "Feed/Legacy/Components/Settings/Permissions";
import { IUpstreamSettingsPaneProps } from "Feed/Legacy/Components/Settings/UpstreamSettingsPane";
import { IViewsProps } from "Feed/Legacy/Components/Settings/Views";
import { HubWebPageDataService } from "Feed/Legacy/DataServices/WebPageDataService";
import { isV2Feed } from "Feed/Legacy/Helpers/FeedCapabilityHelper";
import { filterUpstreamSettingsList } from "Feed/Legacy/Helpers/FilterHelper";
import { isPublicFeed } from "Feed/Legacy/Helpers/ProjectHelper";
import { RoleHelper } from "Feed/Legacy/Helpers/RoleHelper";
import { getUpstreamSourceRowData } from "Feed/Legacy/Helpers/UpstreamHelper";
import { UpstreamHealthStatus } from "Feed/Common/Contracts/WebPage";

export class MapIFeedSettingsStateToProps {
    public static ToFeedDetailsProps(state: IFeedSettingsState): IFeedDetailsPaneProps {
        if (state.feed() == null) {
            return null;
        }

        // note that we're using object destructuring here, because as we extend the retention policy
        // attributes that are supported, we will "unpack" them all in one go.
        // Keep the variable names the same as the returned object's property names
        const {
            retentionPolicyCountLimit,
            retentionPolicyDaysLimit
        } = MapIFeedSettingsStateToProps._getRetentionPolicyDisplayValues(
            state.retentionPolicySettings.retentionPolicyToApply || state.retentionPolicySettings.retentionPolicy
        );

        //Note: Since this variable is only used for the checkbox associated with retentionv2
        // the following variable doesn't affect the retentionv1 code.
        var retentionPolicyInitialActive = false;
        if (retentionPolicyCountLimit !== "" || retentionPolicyDaysLimit !== "") retentionPolicyInitialActive = true;

        const feedDetailsProps: IFeedDetailsPaneProps = {
            pageContext: state.pageContext,
            feedName: state.feedName != null ? state.feedName : state.feed().name,
            feedNameErrorMessage: state.validationErrorBag[FeedSettingsComponents.feedName] || "",
            feedDescription: state.feedDescription != null ? state.feedDescription : state.feed().description || "",
            hideDeletedPackageVersions:
                state.hideDeletedPackageVersions != null
                    ? state.hideDeletedPackageVersions
                    : state.feed().hideDeletedPackageVersions,
            badgesEnabled: state.badgesEnabled != null ? state.badgesEnabled : state.feed().badgesEnabled,
            isUserAdmin: state.isUserAdmin(),
            hasChanges: (): boolean => {
                return this._hasFeedDetailsChanged(state);
            },
            hasValidationErrors: (): boolean => {
                return this._hasFeedDetailsValidationErrors(state);
            },
            isSavingChanges: state.isSavingChanges,
            ...state.retentionPolicySettings,
            retentionPolicyCountErrorMessage:
                state.validationErrorBag[FeedSettingsComponents.feedRetentionPolicyMaxVersions] || "",
            retentionPolicyDaysToKeepErrorMessage:
                state.validationErrorBag[FeedSettingsComponents.feedRetentionPolicyDaysToKeep] || "",
            retentionPolicyCountLimit,
            retentionPolicyDaysLimit: retentionPolicyDaysLimit,
            retentionPolicyMinCountLimit: state.retentionPolicySettings.retentionPolicyMinCountLimit,
            retentionPolicyMaxCountLimit: state.retentionPolicySettings.retentionPolicyMaxCountLimit,
            retentionPolicyMinDaysToKeepLimit: state.retentionPolicySettings.retentionPolicyMinDaysToKeepLimit,
            retentionPolicyMaxDaysToKeepLimit: state.retentionPolicySettings.retentionPolicyMaxDaysToKeepLimit,
            retentionPolicyCountLimitDefault: state.retentionPolicySettings.retentionPolicyCountLimitDefault,
            retentionPolicyDaysToKeepLimitDefault: state.retentionPolicySettings.retentionPolicyDaysToKeepLimitDefault,
            retentionPoliciesActive: retentionPolicyInitialActive,
            upgradeAvailable: state.upgradeAvailable,
            upgradeInProgress: state.upgradeInProgress
        };
        return feedDetailsProps;
    }

    public static ToPermissionsProps(state: IFeedSettingsState): IPermissionsProps {
        if (state.feed() == null) {
            return null;
        }

        const filteredPermissions = this._applyPermissionFilter(state.feedPermissions, state.permissionsFilter);
        const permissionsProps: IPermissionsProps = {
            pageContext: state.pageContext,
            isDataLoadedFromServer: state.isPermissionDataLoadedFromServer,
            isLoading: state.isLoadingPermissions,
            permissions: filteredPermissions,
            hasNoPermissions: state.feedPermissions.length === 0,
            hasNoFilterResults: filteredPermissions.length === 0,
            isUserAdmin: state.isUserAdmin(),
            collectionScopedPermissionIsDisplayed: this._isBuildPermissionDisplayed(state, true /* collection-scope */),
            projectScopedPermissionIsDisplayed: this._isBuildPermissionDisplayed(state, false /* project-scope */),
            projectScopedPermissionExists: this._doesProjectScopedPermissionExists(state.pageContext),
            validationErrorBag: state.validationErrorBag,
            showAddUsersOrGroupsPanel: state.showAddUsersOrGroupsPanel,
            projectCollectionAdminGroupId: state.projectCollectionAdminGroupId,
            isSavingChanges: state.isSavingChanges,
            error: state.error
        };
        return permissionsProps;
    }

    public static ToViewsProps(state: IFeedSettingsState): IViewsProps {
        if (state.feed() == null) {
            return null;
        }

        const allViews = state.views;
        const filteredViews = this._applyViewsFilter(allViews, state.viewsFilter);
        const viewsProps: IViewsProps = {
            pageContext: state.pageContext,
            isDataLoadedFromServer: state.isViewsDataLoadedFromServer,
            isLoading: state.isLoadingViews,
            defaultViewId: state.feed().defaultViewId,
            views: filteredViews,
            viewPermissions: this._getViewPermissions(
                allViews,
                state.viewPermissions,
                state.viewPermissionsToAdd,
                state.viewPermissionsToRemove
            ),
            hasNoViews: allViews.length === 0,
            hasNoFilterResults: filteredViews.length === 0,
            isUserAdmin: state.isUserAdmin(),
            validationErrorBag: state.validationErrorBag,
            showViewPanelDisplay: state.showViewPanel,
            selectedViews: state.selectedViews,
            isViewPanelInEditMode: state.isViewPanelInEditMode,
            isAadUpstreamsFeatureEnabled: state.internalUpstreamSettings.aadUpstreamsEnabled,
            isProjectScopedUpstreamsFeatureEnabled: state.internalUpstreamSettings.isProjectScopedUpstreamsEnabled,
            isSavingChanges: state.isSavingChanges,
            error: state.error,
            isProjectScopedFeed: state.feed().project != null,
            isPublicFeed: isPublicFeed(state.feed()),
            collectionName: this._getCollectionName(state.pageContext),
            feedName: state.feed().name
        };
        return viewsProps;
    }

    public static ToUpstreamSettingsProps(
        state: IFeedSettingsState,
        upstreamSourceStatuses: UpstreamHealthStatus[]
    ): IUpstreamSettingsPaneProps {
        if (state.feed() == null) {
            return null;
        }

        const upstreamRows = getUpstreamSourceRowData(state.pageContext, state.feed(), upstreamSourceStatuses);
        const filteredUpstreamRows = filterUpstreamSettingsList(
            state.upstreamSourceFilter,
            upstreamRows.upstreamSources
        );

        const upstreamSettingsProps: IUpstreamSettingsPaneProps = {
            pageContext: state.pageContext,
            description: isV2Feed(state.feed())
                ? Utils_String.format(
                      PackageResources.UpstreamSettings_Description_V2Capability,
                      PackageResources.AzureArtifacts
                  )
                : PackageResources.UpstreamSettings_Description_NoV2Capability,
            isUserAdmin: state.isUserAdmin(),
            filter: state.upstreamSourceFilter,
            protocolFilterValues: upstreamRows.protocolFilterValues,
            hasNoUpstreamSources: upstreamRows.upstreamSources.length === 0,
            hasNoFilterResults: filteredUpstreamRows.length === 0,
            upstreamSourceRows: filteredUpstreamRows,
            upstreamSourceNameInvalidMessage: state.validationErrorBag[FeedSettingsComponents.upstreamSourceName] || "",
            upstreamSourceLocationInvalidMessage:
                state.validationErrorBag[FeedSettingsComponents.upstreamSourceLocation] || "",
            displayAddUpstreamPanel: state.displayAddUpstreamPanel,
            currentFeed: state.feed(),
            availableFeeds: state.feeds,
            internalUpstreamSettings: state.internalUpstreamSettings,
            activeUpstreamSources: state
                .feed()
                .upstreamSources.filter((upstreamSource: UpstreamSource) => upstreamSource.deletedDate == null),
            isSavingChanges: state.isSavingChanges,
            error: state.error,
            isCustomPublicUpstreamsFeatureEnabled:
                state.isCustomPublicUpstreamsFeatureEnabled && isV2Feed(state.feed()),
            isProjectScopedUpstreamsEnabled: state.isProjectScopedUpstreamsFeatureEnabled,
            upstreamSourceLimit: state.upstreamSourceLimit,
            isServiceEndpointUpstreamsFeatureEnabled: state.isServiceEndpointUpstreamsFeatureEnabled
        };
        return upstreamSettingsProps;
    }

    private static _hasFeedDetailsChanged(state: IFeedSettingsState): boolean {
        return (
            state.feedName != null ||
            state.feedDescription != null ||
            state.hideDeletedPackageVersions != null ||
            state.retentionPolicySettings.retentionPolicyToApply != null ||
            state.badgesEnabled != null
        );
    }

    private static _hasFeedDetailsValidationErrors(state: IFeedSettingsState): boolean {
        return (
            state.validationErrorBag[FeedSettingsComponents.feedName] != null ||
            state.validationErrorBag[FeedSettingsComponents.feedRetentionPolicyMaxVersions] != null
        );
    }

    private static _applyPermissionFilter(permissions: FeedPermission[], permissionsFilter: Filter): FeedPermission[] {
        const userOrGroupFilterKeyword = permissionsFilter.getFilterItemValue<string>(
            SettingsFilterItemKey.userOrGroup
        );
        const rolesFilter = permissionsFilter.getFilterItemValue<string[]>(SettingsFilterItemKey.role);

        if (
            (userOrGroupFilterKeyword == null || userOrGroupFilterKeyword === "") &&
            (rolesFilter == null || rolesFilter.length === 0)
        ) {
            return [].concat(permissions);
        }

        const filteredPermissions = [];
        permissions.forEach((permission: FeedPermission) => {
            if (
                userOrGroupFilterKeyword != null &&
                !Utils_String.caseInsensitiveContains(permission.displayName, userOrGroupFilterKeyword)
            ) {
                return;
            }

            if (rolesFilter != null && rolesFilter.length !== 0 && !this._hasRole(permission, rolesFilter)) {
                return;
            }

            filteredPermissions.push(permission);
        });

        return filteredPermissions;
    }

    private static _hasRole(permission: FeedPermission, rolesFilter: string[]): boolean {
        return rolesFilter.some((role: string) => {
            return permission.role === RoleHelper.stringToRole(role);
        });
    }

    /**
     * Give list of view permissions that needs to be displayed in grid
     * @param viewsInFeed - Initial set of views available on feed
     * @param viewPermissions - Initial set of permissions set on feed, key is viewId
     * @param viewPermissionsToAdd - User wants to add these permissions, key is viewId
     * @param viewPermissionsToRemove - User wants to remove these permissions, key is viewId
     */
    private static _getViewPermissions(
        viewsInFeed: FeedView[],
        viewPermissions: { [key: string]: FeedPermission[] },
        viewPermissionsToAdd: { [key: string]: FeedPermission[] },
        viewPermissionsToRemove: { [key: string]: FeedPermission[] }
    ): { [key: string]: FeedPermission[] } {
        const permissions: { [key: string]: FeedPermission[] } = {};

        for (const view of viewsInFeed) {
            permissions[view.id] = [];

            const permissionsToRemoveMap: { [key: string]: string } = {};
            if (viewPermissionsToRemove[view.id]) {
                for (const permission of viewPermissionsToRemove[view.id]) {
                    permissionsToRemoveMap[permission.identityId] = permission.identityId;
                }
            }

            // add existing permissions, excluding those to be removed
            if (viewPermissions[view.id]) {
                for (const permission of viewPermissions[view.id]) {
                    if (permissionsToRemoveMap[permission.identityId] == null) {
                        permissions[view.id].push(permission);
                    }
                }
            }

            // add new permissions
            if (viewPermissionsToAdd[view.id]) {
                permissions[view.id] = permissions[view.id].concat(...viewPermissionsToAdd[view.id]);
            }
        }

        return permissions;
    }

    private static _applyViewsFilter(views: FeedView[], viewsFilter: Filter): FeedView[] {
        const viewsFilterKeyword = viewsFilter.getFilterItemValue<string>(SettingsFilterItemKey.views);

        if (viewsFilterKeyword == null || viewsFilterKeyword === "") {
            return views;
        }

        const filteredViews: FeedView[] = [];
        views.forEach((view: FeedView) => {
            if (viewsFilterKeyword != null && !Utils_String.caseInsensitiveContains(view.name, viewsFilterKeyword)) {
                return;
            }

            filteredViews.push(view);
        });

        return filteredViews;
    }

    /**
     * Tells whether Build permission is displayed in Grid or not
     * @param state - FeedSettingsState
     * @param isCollectionScope - TRUE for collection-scope FALSE for project-scope
     */
    private static _isBuildPermissionDisplayed(state: IFeedSettingsState, isCollectionScope: boolean): boolean {
        const webPageDataService = state.pageContext.getService<HubWebPageDataService>("IFeedWebPageDataService");

        let identityId: string;
        if (isCollectionScope) {
            identityId = webPageDataService.getCollectionBuildIdentity().id;
        } else {
            const projectBuildIdentity = webPageDataService.getProjectBuildIdentity();
            if (projectBuildIdentity == null) {
                return false;
            }
            identityId = projectBuildIdentity.id;
        }

        return this._identityIdExists(identityId, state.feedPermissions);
    }

    /**
     * Check whether <Project Name> Build Service (<account/collection name>) identity exists or not
     * This is created on-demand based on Build Job Authorization Scope set to Project Collection or Current Project
     */
    private static _doesProjectScopedPermissionExists(pageContext: IVssPageContext): boolean {
        const webPageDataService = pageContext.getService<HubWebPageDataService>("IFeedWebPageDataService");
        return webPageDataService.getProjectBuildIdentity() != null;
    }

    private static _getCollectionName(pageContext: IVssPageContext): string {
        return pageContext.getService<IVssPageService>("IVssPageService").getData().hostName;
    }

    private static _identityIdExists(identityId: string, permissions: FeedPermission[]): boolean {
        const index: number = findIndex(permissions, (permission: FeedPermission) => {
            return permission.identityId === identityId;
        });
        return index >= 0;
    }

    //Note: daysToKeepRecentlyDownloadedPackages has been abreviated to days to avoid cluttering the UI
    //This is a different value from age - which is no longer in use but cannot be removed from the contract.
    private static _getRetentionPolicyDisplayValues(
        policy: FeedRetentionPolicy
    ): { retentionPolicyCountLimit: string; retentionPolicyAgeLimit?: string; retentionPolicyDaysLimit: string } {
        let count = "";
        let days = "";
        let age = "";

        if (policy) {
            count = policy.countLimit != null ? Number(policy.countLimit).toString() : "";
            days =
                policy.daysToKeepRecentlyDownloadedPackages != null
                    ? Number(policy.daysToKeepRecentlyDownloadedPackages).toString()
                    : "";
            age = policy.ageLimitInDays != null ? Number(policy.ageLimitInDays).toString() : "";
        }

        return {
            retentionPolicyCountLimit: count,
            retentionPolicyDaysLimit: days,
            retentionPolicyAgeLimit: age
        };
    }
}
